<script setup>
import { ref, computed, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import UiModal from '@/Components/UI/UiModal.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  open:    { type: Boolean, default: false },
  vehicle: { type: Object,  required: true },
  sources: { type: Array,   default: () => [] },
  // Optional prefill used by the Renew sub-rental flow on a released vehicle.
  // { source_id, rate_cents, rate_unit }
  prefill: { type: Object,  default: null }
})
const emit = defineEmits(['update:open'])
const { t } = useI18n()

const sourceId   = ref('')
const newName    = ref('')
const start_on   = ref(new Date().toISOString().slice(0, 10))
const end_on     = ref('')
const amount     = ref('')
const rate_unit  = ref('day')
const deposit    = ref('')
const submitting = ref(false)

function centsToInput(cents) {
  if (!cents) return ''
  const v = cents / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

watch(() => props.open, (val) => {
  if (val) {
    const p = props.prefill || {}
    sourceId.value = p.source_id ? String(p.source_id) : ''
    newName.value = ''
    start_on.value = new Date().toISOString().slice(0, 10)
    end_on.value = ''
    amount.value = centsToInput(p.rate_cents)
    rate_unit.value = p.rate_unit || 'day'
    deposit.value = centsToInput(p.deposit_cents)
  }
})

const sourceOptions = computed(() => [
  { value: '',        label: '—' },
  ...props.sources.map(s => ({ value: String(s.id), label: s.name })),
  { value: '__new__', label: t('suppliers.markRented.newSource') }
])

const rateUnitOptions = computed(() => [
  { value: 'day',   label: t('sub_rentals.units.day') },
  { value: 'week',  label: t('sub_rentals.units.week') },
  { value: 'month', label: t('sub_rentals.units.month') }
])

function close() {
  emit('update:open', false)
}

function submit() {
  const cents = Math.round(parseFloat(amount.value) * 100)
  if (!cents) return
  const depositCents = Math.round(parseFloat(deposit.value || 0) * 100) || 0
  submitting.value = true

  const payload = {
    vehicle_sub_rental: {
      start_on:      start_on.value,
      end_on:        end_on.value || null,
      rate_cents:    cents,
      rate_unit:     rate_unit.value,
      deposit_cents: depositCents
    }
  }

  if (sourceId.value === '__new__' && newName.value.trim()) {
    // FUTURE ENHANCEMENT: inline-supplier creation requires
    // VehicleSourcesController#create to set flash[:created_source_id] (or
    // similar) so we can grab the new id after the redirect. Right now the
    // controller only sets `notice:`, so this branch will silently bail.
    // The v1 happy path is "pick an existing supplier" via the dropdown.
    router.post('/vehicle_sources', { vehicle_source: { name: newName.value.trim() } }, {
      preserveScroll: true,
      onSuccess: (page) => {
        const newId = page.props.flash?.created_source_id
        if (!newId) { submitting.value = false; return }
        payload.vehicle_sub_rental.source_id = newId
        router.post(`/vehicles/${props.vehicle.id}/sub_rentals`, payload, {
          onFinish: () => { submitting.value = false; close() }
        })
      },
      onError: () => { submitting.value = false }
    })
  } else {
    payload.vehicle_sub_rental.source_id = sourceId.value
    router.post(`/vehicles/${props.vehicle.id}/sub_rentals`, payload, {
      preserveScroll: true,
      onFinish: () => { submitting.value = false; close() }
    })
  }
}
</script>

<template>
  <UiModal :open="open" :title="t('suppliers.markRented.title')" @close="close">
    <form class="space-y-3" @submit.prevent="submit">
      <div>
        <label class="block text-sm mb-1">{{ t('suppliers.markRented.source') }}</label>
        <UiSelect v-model="sourceId" :options="sourceOptions" required />
        <UiInput
          v-if="sourceId === '__new__'"
          v-model="newName"
          class="mt-2"
          :placeholder="t('suppliers.markRented.newSourcePlaceholder')"
          required
        />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-sm mb-1">{{ t('sub_rentals.startOn') }}</label>
          <UiInput v-model="start_on" type="date" required />
        </div>
        <div>
          <label class="block text-sm mb-1">{{ t('sub_rentals.endOn') }}</label>
          <UiInput v-model="end_on" type="date" />
          <p class="text-xs text-[color:var(--muted)] mt-1">{{ t('sub_rentals.endOnHint') }}</p>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-sm mb-1">{{ t('sub_rentals.rateAmount') }}</label>
          <UiInput v-model="amount" type="number" min="1" step="1" required />
        </div>
        <div>
          <label class="block text-sm mb-1">{{ t('sub_rentals.rateUnit') }}</label>
          <UiSelect v-model="rate_unit" :options="rateUnitOptions" />
        </div>
      </div>

      <div>
        <label class="block text-sm mb-1">{{ t('sub_rentals.deposit') }}</label>
        <UiInput v-model="deposit" type="number" min="0" step="1" />
        <p class="text-xs text-[color:var(--muted)] mt-1">{{ t('sub_rentals.depositHint') }}</p>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <UiButton type="button" variant="ghost" @click="close">
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton type="submit" variant="primary" :loading="submitting">
          {{ t('suppliers.markRented.create') }}
        </UiButton>
      </div>
    </form>
  </UiModal>
</template>
